/**
 * Addresses for GA4: the query redacted, the fragment dropped. Used for the
 * page_view (pageView.ts) and for page_location / page_referrer on EVERY
 * other push (dataLayer.ts) — otherwise gtag fills them from the raw
 * document.location (?supq=, ?session_id=, ?token= …). utm_*, gclid and ref
 * stay: GA4 reads campaign and Ads attribution from page_location.
 */
import { redactEmails, redactGiftTokens } from "@/features/activityLog/lib/sanitize";

// Lowercase: keys are compared lowercased.
export const REDACTED_PARAMS = new Set([
    "q",
    "return",
    "redeem",
    "session_id",
    "inv_id",
    "code",
    "hash",
    "token",
    "id_token",
    "email",
    "who",
    // Not in the original list, same reasons: `from` carries the encoded
    // return address (with its ?q=), `invite` is a live-call invitation.
    "from",
    "invite",
    // The SUP question in the address (SupV2: ?sup=list&supq=<text>) — SUP text
    // goes to GA4 only through cleanText in the `search` event.
    "supq",
    // The consultations e-mail confirmation secret (ConsultationsProfileSection).
    "consultationsemailtoken",
    // Free text from shared lecture links (LecturesPageContent).
    "title",
]);

/** Same-site address for GA4: redacted query, no fragment. "" when unusable. */
export const redactUrl = (href: string, base?: string): string => {
    let u: URL;
    try {
        u = new URL(href, base || window.location.href);
    } catch {
        return "";
    }
    if (u.protocol !== "http:" && u.protocol !== "https:") return "";
    const kept = new URLSearchParams();
    u.searchParams.forEach((value, key) => {
        if (REDACTED_PARAMS.has(key.toLowerCase())) return;
        kept.append(redactEmails(key), redactGiftTokens(redactEmails(value)));
    });
    const qs = kept.toString();
    return `${u.origin}${redactGiftTokens(redactEmails(u.pathname))}${qs ? `?${qs}` : ""}`;
};

/** A referrer: our own pages redacted like page_location, other sites origin + path only. */
export const redactReferrer = (href: string): string => {
    if (!href) return "";
    let u: URL;
    try {
        u = new URL(href);
    } catch {
        return "";
    }
    if (u.protocol !== "http:" && u.protocol !== "https:") return "";
    let own = false;
    try {
        own = u.origin === window.location.origin;
    } catch {
        own = false;
    }
    return own ? redactUrl(href) : `${u.origin}${redactEmails(u.pathname)}`;
};
